/**
 * Bytebeat Audio Engine
 *
 * Evaluates bytebeat expressions using the Web Audio API's ScriptProcessorNode.
 * Output byte (0-255) is normalised to [-1, 1] for audio output.
 *
 * Compilation is delegated to bytebeatCompiler.ts which uses indirect eval
 * so generated code always runs in non-strict (sloppy) global scope.
 */

import { compileFn } from './bytebeatCompiler';

export { validateFn as testBytebeatCode } from './bytebeatCompiler';

export interface BytebeatEngineOptions {
  code: string;
  bitrate: number;
  args?: Record<string, number>;
  onError?: (err: string) => void;
}

export class BytebeatEngine {
  private ctx: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private gainNode: GainNode | null = null;
  private t = 0;
  private fn: ((t: number, a: Record<string, number>) => number) | null = null;
  private args: Record<string, number> = {};
  private bitrate: number;
  private _playing = false;
  private onError: (err: string) => void;
  private resampleRatio = 1;
  private code: string;

  constructor(options: BytebeatEngineOptions) {
    this.bitrate = options.bitrate;
    this.args = options.args ?? {};
    this.onError = options.onError ?? (() => {});
    this.code = options.code;
    this.compile(options.code, this.args);
  }

  private compile(code: string, args: Record<string, number>): boolean {
    this.fn = compileFn(code, Object.keys(args), this.onError);
    return this.fn !== null;
  }

  updateArgs(args: Record<string, number>): void {
    this.args = args;
    this.compile(this.code, args);
  }

  get playing(): boolean { return this._playing; }

  async play(): Promise<void> {
    if (this._playing) return;
    if (!this.fn) { this.onError('No valid bytebeat code compiled.'); return; }
    if (!this.ctx) this.ctx = new AudioContext();
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const sampleRate = this.ctx.sampleRate;
    this.resampleRatio = sampleRate / this.bitrate;

    const bufferSize = 4096;
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = 0.8;
    this.gainNode.connect(this.ctx.destination);

    this.processor = this.ctx.createScriptProcessor(bufferSize, 0, 1);
    this.processor.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      const fn = this.fn;
      if (!fn) { output.fill(0); return; }
      for (let i = 0; i < output.length; i++) {
        const tInt = Math.floor(this.t);
        let sample = 128;
        try {
          sample = fn(tInt, this.args);
          if (!isFinite(sample)) sample = 128;
        } catch { sample = 128; }
        output[i] = (sample - 128) / 128;
        this.t += 1 / this.resampleRatio;
      }
    };

    this.processor.connect(this.gainNode);
    this._playing = true;
  }

  pause(): void {
    if (!this._playing) return;
    this.processor?.disconnect();
    this.processor = null;
    this._playing = false;
  }

  stop(): void { this.pause(); this.t = 0; }

  destroy(): void {
    this.stop();
    this.gainNode?.disconnect();
    this.gainNode = null;
    this.ctx?.close();
    this.ctx = null;
  }

  setVolume(vol: number): void {
    if (this.gainNode) this.gainNode.gain.value = Math.max(0, Math.min(1, vol));
  }

  getTime(): number { return this.t; }
  reset(): void { this.t = 0; }
}
